import styles from './ActionModal.module.scss'
import {BaseModal} from '../../../../../../components/BaseModal/BaseModal'
import {
  WuButton,
  WuCard,
  WuCardHeader,
  WuCombobox,
  WuDatePicker,
  WuHeading,
  WuIcon,
  WuInput,
  WuSelect,
  WuTextarea,
} from '@npm-questionpro/wick-ui-lib'
import {useMemo, useState} from 'react'
import {IInitiative, ITask} from '../../../../../../types'
import {initiativesSelectors} from '../../../../../../store/initiatives/initiativesSelectors'
import {initiativesApi} from '../../../../../Initiatives/api/initiativesApi'
import {tasksApi} from '../../../../../Tasks/api/tasksApi'
import {checkValidation} from '../../../../../../utils'
import {useWt} from '@npm-questionpro/wick-ui-lib'
import {GLOBAL} from '../../../../../../Translation/English/global'
import {DIALOG} from '../../../../../../Translation/English/dialog'
import {TASK} from '../../../../../../Translation/English/task'

interface IActionModalProps {
  showModal: boolean
  closeModal: (topicName: string) => void
  data: {
    topic: string
    action?: string
    description?: string
  } | null
}

export const ActionModal: React.FC<IActionModalProps> = ({
  showModal,
  closeModal,
  data,
}) => {
  const wt = useWt()
  const {mutateAsync: createTask} = tasksApi.useCreateTask()
  const currentFilters = initiativesSelectors.useCurrentFilters()
  const {data: tableData} = initiativesApi.useInitiativesTable({
    page: 1,
    ...currentFilters,
  })
  const initiativesList = useMemo(() => {
    const list = (tableData?.initiatives || []) as IInitiative[]
    return [...list].sort((a, b) =>
      (a?.name || '').localeCompare(b?.name || ''),
    )
  }, [tableData])

  const [task, setTask] = useState<ITask>({
    name: data?.action || '',
    description: data?.description || '',
    dueDate: new Date(new Date().setDate(new Date().getDate() + 7)),
    responsible: {},
    people: [],
  })
  const [selectedInitiative, setSelectedInitiative] = useState<{
    initiative: IInitiative
  } | null>(null)

  const getHeader = (): React.ReactElement => {
    return (
      <div className={styles.title}>
        {wt(DIALOG.actionRecommendation)}: {data?.topic}
      </div>
    )
  }

  const handleCreateTask = async (): Promise<void> => {
    const {errors: _errors, error} = checkValidation({
      name: task.name,
    })
    if (error) {
      //Toast error can be shown here using the error.message or a generic error message
      return
    }
    try {
      await createTask({
        ...task,
        initiativeId: selectedInitiative?.initiative.id,
        status: 'PENDING',
      })
      closeModal(data?.topic || '')
    } catch {
      // error handled by store
    }
  }

  const setInitiative = (
    e: IInitiative | null | Array<IInitiative> | undefined,
  ): void => {
    setSelectedInitiative({initiative: e as IInitiative})
    setTask({
      ...task,
      responsible: {},
      people: [],
    })
  }

  return (
    <BaseModal
      visible={showModal}
      headerTitle={getHeader()}
      onCloseModal={() => closeModal('')}
      maxWidth="750px"
      footer={
        <div className={styles.footer}>
          <WuButton variant="secondary" onClick={() => closeModal('')}>
            {wt(GLOBAL.cancel)}
          </WuButton>
          <WuButton
            variant="primary"
            disabled={!selectedInitiative?.initiative.id}
            onClick={handleCreateTask}
          >
            {wt(GLOBAL.saveChanges)}
          </WuButton>
        </div>
      }
    >
      <div className={styles.actionModal}>
        <div className={styles.actionModalInitiativeBlock}>
          <WuCombobox
            Label={wt(DIALOG.selectInitiative)}
            accessorKey={{
              label: 'name',
              value: 'id',
            }}
            data={initiativesList}
            onSelect={setInitiative}
            placeholder={wt(DIALOG.selectInitiativePlaceholder) || ''}
            enableSearch
          />
        </div>
        <WuCard rounded>
          <WuCardHeader className={styles.cardHeading}>
            <WuHeading size="sm">{wt(DIALOG.recommendedAction)}</WuHeading>
            <WuIcon icon="wc-ai" className={styles.aiIcon} />
          </WuCardHeader>
          <div className={styles.actionModalActionText}>{data?.action}</div>
        </WuCard>
        {selectedInitiative?.initiative.id && (
          <div className={styles.actionModalCreateTask}>
            <WuInput
              Label={wt(GLOBAL.name)}
              labelPosition="top"
              onChange={e => setTask({...task, name: e.target.value})}
              placeholder={wt(GLOBAL.typeHere) || ''}
              type="text"
              variant="flat"
              value={task.name}
            />
            <WuTextarea
              Label={wt(GLOBAL.description)}
              labelPosition="top"
              onChange={e => setTask({...task, description: e.target.value})}
              placeholder={wt(GLOBAL.typeHere) || ''}
              rows={3}
              variant="flat"
              value={task.description ?? ''}
              className={styles.actionModalCreateTaskDescp}
            />
            <div className={styles.actionModalCreateTaskBlock}>
              <WuDatePicker
                formatString="MMMM d, yyyy"
                value={task.dueDate}
                onChange={date =>
                  setTask({...task, dueDate: date || task.dueDate})
                }
                variant="outlined"
                className="wui-date-picker"
                Label={wt(GLOBAL.dueDate)}
              />
              <WuSelect
                Label={wt(GLOBAL.owner)}
                accessorKey={{
                  label: 'username',
                  value: 'emailAddress',
                }}
                data={selectedInitiative?.initiative.people || []}
                onSelect={e =>
                  setTask({
                    ...task,
                    responsible: Array.isArray(e) ? (e[0] ?? {}) : e,
                  })
                }
                placeholder={`${wt(TASK.selectOwner)}`}
                value={task.responsible}
              />
            </div>
            <WuSelect
              Label={wt(GLOBAL.contributors)}
              accessorKey={{
                label: 'username',
                value: 'emailAddress',
              }}
              data={selectedInitiative?.initiative.people || []}
              onSelect={e =>
                setTask({...task, people: Array.isArray(e) ? e : [e]})
              }
              placeholder={`${wt(TASK.selectContributor)}`}
              multiple
              className={styles.actionModalCreateTaskMultiselect}
              value={task.people}
            />
          </div>
        )}
      </div>
    </BaseModal>
  )
}
